import { useEffect, useState, memo, useRef } from 'react'
import { FaInfoCircle, FaTrash, FaPlus, FaDownload, FaUpload } from 'react-icons/fa'
import { Toaster, toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { HookResult, createDefaultHookResult } from '../../hooks/hookTypes'
import {
  useAddAccessToProject,
  useUpdateAccessToProject,
  useDeleteAccessToProject,
  useUpdateProjectSystemAgentEnabled,
  useUpdateProjectDetails,
} from '../../stores/projectsStore'

export enum AccessLevel {
  Read = 'read',
  Edit = 'edit',
  Full = 'full',
}

export interface ProjectSettingsProps {
  projectId: string
  settings: any
  accessRules: any[]
  closeModal: () => void
  onAccessRulesUpdate: () => void
  systemAgentEnabled: boolean | null
  onSystemAgentEnabledChange?: () => void
  projectName?: string
  projectDescription?: string
  projectType?: string
  organizationName?: string
}

function ProjectSettings({
  projectId,
  settings,
  accessRules,
  closeModal,
  onAccessRulesUpdate,
  systemAgentEnabled,
  onSystemAgentEnabledChange,
  projectName: projectNameProp,
  projectDescription: projectDescriptionProp,
  projectType,
  organizationName,
}: ProjectSettingsProps) {
  // Determine if this is an organization project based on either projectType or organizationName
  const isOrganizationProject = projectType === 'collaboration' || organizationName
  const [emailInput, setEmailInput] = useState('')
  const [selectedAccessLevel, setSelectedAccessLevel] = useState<AccessLevel>(
    AccessLevel.Read
  )
  const [editedAccessLevels, setEditedAccessLevels] = useState([])
  const [agentEnabled, setAgentEnabled] =
    useState<boolean>(!!systemAgentEnabled)
  const [isSystemAgentChanged, setIsSystemAgentChanged] = useState(false)
  const [projectName, setProjectName] = useState(projectNameProp || settings?.projectName || '')
  const [projectDescription, setProjectDescription] = useState(projectDescriptionProp || settings?.projectDescription || '')
  const [isProjectDetailsChanged, setIsProjectDetailsChanged] = useState(false)

  // Environment variables state
  const [environmentVars, setEnvironmentVars] = useState([])
  const [newEnvKey, setNewEnvKey] = useState('')
  const [newEnvValue, setNewEnvValue] = useState('')
  const [activeTab, setActiveTab] = useState('general')

  // Import/Export state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)

  // Creativeness state
  const [creativeness, setCreativeness] = useState(
    Array.isArray(settings) && settings[0] && typeof settings[0].creativeness === 'number'
      ? settings[0].creativeness
      : (typeof settings?.creativeness === 'number' ? settings.creativeness : 50)
  )

const addAccessToProject = (useAddAccessToProject() ||
    createDefaultHookResult()) as HookResult
  const updateAccessToProject = (useUpdateAccessToProject() ||
    createDefaultHookResult()) as HookResult
  const deleteAccessFromProject = (useDeleteAccessToProject() ||
    createDefaultHookResult()) as HookResult
  const updateProjectSystemAgentEnabled =
    (useUpdateProjectSystemAgentEnabled() ||
      createDefaultHookResult()) as HookResult
  const useUpdateProjectDetailsHook = useUpdateProjectDetails() || createDefaultHookResult()

  useEffect(() => {
    setAgentEnabled(!!systemAgentEnabled)
  }, [systemAgentEnabled])

  // Fetch environment variables
  const fetchEnvironmentVars = async () => {
    try {
      const response = await fetch(`/api/projects/${projectId}/environment-vars`)
      if (response.ok) {
        const data = await response.json()
        setEnvironmentVars(data.environmentVars || [])
      }
    } catch (error) {
      console.error('Error fetching environment variables:', error)
    }
  }

  useEffect(() => {
    if (projectId) {
      fetchEnvironmentVars()
    }
  }, [projectId])

  // Add environment variable
  const addEnvironmentVar = async () => {
    if (!newEnvKey.trim() || !newEnvValue.trim()) {
      toast.error('Both key and value are required')
      return
    }

    try {
      const response = await fetch(`/api/projects/${projectId}/environment-vars`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: newEnvKey, value: newEnvValue })
      })

      if (response.ok) {
        toast.success('Environment variable added')
        setNewEnvKey('')
        setNewEnvValue('')
        fetchEnvironmentVars()
      } else {
        toast.error('Failed to add environment variable')
      }
    } catch (error) {
      toast.error('Error adding environment variable')
    }
  }

  // Delete environment variable
  const deleteEnvironmentVar = async (varId: string) => {
    if (!window.confirm('Are you sure you want to delete this environment variable?')) {
      return
    }

    try {
      const response = await fetch(`/api/projects/${projectId}/environment-vars/${varId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        toast.success('Environment variable deleted')
        fetchEnvironmentVars()
      } else {
        toast.error('Failed to delete environment variable')
      }
    } catch (error) {
      toast.error('Error deleting environment variable')
    }
  }

  // Export project data
  const exportProject = async () => {
    if (isExporting) return
    
    setIsExporting(true)
    try {
      const response = await fetch(`/api/projects/${projectId}/export`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        const projectData = await response.json()
        
        // Create export object with complete project data (metadata for reference + importable contents)
        const exportData = {
          // Project metadata (exported for reference, not imported back)
          projectName,
          projectDescription,
          projectType,
          organizationName,
          // Project contents (these are imported when importing)
          settings,
          environmentVars,
          creativeness,
          // Export metadata
          exportedAt: new Date().toISOString(),
          exportVersion: '1.0',
          ...projectData
        }

        // Create and download the JSON file
        const dataStr = JSON.stringify(exportData, null, 2)
        const dataBlob = new Blob([dataStr], { type: 'application/json' })
        const url = URL.createObjectURL(dataBlob)
        
        const link = document.createElement('a')
        link.href = url
        link.download = `${projectName || 'project'}_export_${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        toast.success('Project exported successfully')
      } else {
        toast.error('Failed to export project')
      }
    } catch (error) {
      console.error('Error exporting project:', error)
      toast.error('Error exporting project')
    } finally {
      setIsExporting(false)
    }
  }

  // Import project data
  const importProject = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.type !== 'application/json') {
      toast.error('Please select a valid JSON file')
      return
    }

    setIsImporting(true)
    try {
      const fileText = await file.text()
      const importData = JSON.parse(fileText)

      // Validate the import data structure
      if (!importData.exportVersion) {
        toast.error('Invalid project export file format')
        return
      }

      // Confirm import action
      const confirmMessage = `This will import project contents (nodes, flows, settings, and environment variables) from "${importData.projectName || 'Unknown Project'}". Project name, description, and system settings will NOT be changed. Nodes and flows will be added to your project immediately. Continue?`
      if (!window.confirm(confirmMessage)) {
        return
      }

      // Update local state with imported data (ONLY project contents, NOT core settings like name/description/systemAgent)
      let hasCreativenessChange = false
      let hasContentChange = false
      
      // Import creativeness from settings if available
      if (importData.settings) {
        if (Array.isArray(importData.settings) && importData.settings[0] && typeof importData.settings[0].creativeness === 'number') {
          setCreativeness(importData.settings[0].creativeness)
          hasCreativenessChange = true
        } else if (typeof importData.settings?.creativeness === 'number') {
          setCreativeness(importData.settings.creativeness)
          hasCreativenessChange = true
        }
      }
      // Fallback to direct creativeness property
      if (!hasCreativenessChange && typeof importData.creativeness === 'number') {
        setCreativeness(importData.creativeness)
        hasCreativenessChange = true
      }
      
      // Import environment variables
      if (importData.environmentVars && Array.isArray(importData.environmentVars)) {
        setEnvironmentVars(importData.environmentVars)
        hasContentChange = true
      }

      // Import nodes and edges directly to database
      let hasNodesOrEdges = false
      let importedNodesCount = 0
      let importedEdgesCount = 0
      
      if ((importData.nodes && Array.isArray(importData.nodes) && importData.nodes.length > 0) ||
          (importData.edges && Array.isArray(importData.edges) && importData.edges.length > 0)) {
        hasNodesOrEdges = true
        importedNodesCount = importData.nodes?.length || 0
        importedEdgesCount = importData.edges?.length || 0
        
        try {
          const importResponse = await fetch(`/api/projects/${projectId}/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              importData: {
                nodes: importData.nodes || [],
                edges: importData.edges || [],
                environmentVars: importData.environmentVars || [],
                settings: importData.settings,
                creativeness: importData.creativeness,
                exportVersion: importData.exportVersion
              }
            })
          })
          
          if (!importResponse.ok) {
            const errorData = await importResponse.json()
            throw new Error(errorData.error || 'Failed to import nodes and edges')
          }
          
          const result = await importResponse.json()
          console.log('Successfully imported:', result.imported)
        } catch (error) {
          console.error('Error importing nodes/edges:', error)
          toast.error(`Failed to import nodes and flows: ${error.message}`)
          return
        }
      }

      // Mark as changed so user can save (only if we actually imported settings)
      if (hasCreativenessChange || hasContentChange) {
        setIsProjectDetailsChanged(true)
      }

      // Show different success message based on what was imported
      if (hasNodesOrEdges) {
        toast.success(`Project contents imported successfully! Imported ${importedNodesCount} nodes and ${importedEdgesCount} edges. Settings and environment variables updated. Refresh the flow to see imported nodes.`)
      } else {
        toast.success('Project settings and environment variables imported successfully. Your original data is safe - refresh page to discard changes, or click Save to apply them.')
      }
    } catch (error) {
      console.error('Error importing project:', error)
      toast.error('Error importing project file')
    } finally {
      setIsImporting(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  // Update state whenever settings or props change
  useEffect(() => {
    if (projectNameProp !== undefined) setProjectName(projectNameProp)
    else if (settings) setProjectName(settings.projectName || '')
    if (projectDescriptionProp !== undefined) setProjectDescription(projectDescriptionProp)
    else if (settings) setProjectDescription(settings.projectDescription || '')
    // Update creativeness from settings[0] if available
    if (settings) {
      if (Array.isArray(settings) && settings[0] && typeof settings[0].creativeness === 'number') {
        setCreativeness(settings[0].creativeness)
      } else if (typeof settings?.creativeness === 'number') {
        setCreativeness(settings.creativeness)
      }
    }
  }, [settings, projectNameProp, projectDescriptionProp])

  console.log(accessRules)

  const showToast = (
    msg: string,
    severity: 'success' | 'error' = 'success'
  ) => {
    if (severity === 'success') {
      toast.success(msg)
    } else {
      toast.error(msg)
    }
  }

  const handleProjectDetailsChange = () => {
    setIsProjectDetailsChanged(true)
  }

  const onAddNewRule = () => {
    console.log('ACCESS_LEVEL_IS: ', selectedAccessLevel)
    addAccessToProject.execute({
      id: projectId,
      data: { projectId, email: emailInput, accessLevel: selectedAccessLevel },
      onSuccess: () => {
        showToast('New access rule added successfully')
        onAccessRulesUpdate()
        setEmailInput('')
        setSelectedAccessLevel(AccessLevel.Read)
      },
      onError: (error) => {
        showToast('Error adding new access rule', 'error')
        console.error('Error adding new access rule:', error)
      },
    })
  }

  const onAccessLevelChange = (rule: any, accessLevel: AccessLevel) => {
    updateEditBuffer(rule, accessLevel)
  }

  const updateEditBuffer = (rule: any, newAccessLevel: AccessLevel) => {
    const existingRuleIndex = editedAccessLevels.findIndex(
      (_rule: any) => rule.id === _rule.id
    )

    if (existingRuleIndex !== -1) {
      const updatedRules = [...editedAccessLevels]
      updatedRules[existingRuleIndex] = { ...rule, accessLevel: newAccessLevel }
      console.log('updatedRules:', updatedRules)
      setEditedAccessLevels(updatedRules)
    } else {
      setEditedAccessLevels([
        ...editedAccessLevels,
        { ...rule, accessLevel: newAccessLevel },
      ])
    }
  }

  // New helper function to get current access level (either from edited state or original)
  const getCurrentAccessLevel = (rule: any): AccessLevel => {
    const editedRule = editedAccessLevels.find(
      (_rule: any) => _rule.id === rule.id
    )
    return editedRule ? editedRule.accessLevel : rule.accessLevel
  }

  const onSystemAgentToggle = () => {
    setAgentEnabled(!agentEnabled)
    setIsSystemAgentChanged(true)
  }

  const handleCreativenessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreativeness(Number(e.target.value))
    setIsProjectDetailsChanged(true)
  }

  const getCreativenessDescription = (level: number) => {
    if (level <= 10) return 'Very strict, deterministic, and literal. Output will be highly predictable and safe.'
    if (level <= 30) return 'Conservative and focused. Output will be mostly safe, with some minor variation.'
    if (level <= 50) return 'Balanced. Output will be a mix of creativity and reliability.'
    if (level <= 70) return 'Creative and open. Output will be more original, with some risk of unexpected ideas.'
    if (level <= 90) return 'Highly creative. Output will be imaginative, surprising, and less constrained.'
    return 'Maximum creativity! Output may be wild, experimental, and very unpredictable.'
  }

  const onSettingsSave = () => {
    // Save access levels changes if any
    if (editedAccessLevels.length > 0) {
      updateAccessToProject.execute({
        id: projectId,
        data: { projectId, editedAccessLevels },
        onSuccess: () => {
          setEditedAccessLevels([])
          showToast('Access rules saved successfully')
          onAccessRulesUpdate()
        },
        onError: (error) => {
          showToast('Error saving access rules', 'error')
          console.error('Error saving access rules:', error)
        },
      })
    }

    // Save system agent setting if changed
    if (isSystemAgentChanged) {
      updateProjectSystemAgentEnabled.execute({
        id: projectId,
        data: { systemAgentEnabled: agentEnabled },
        onSuccess: () => {
          setIsSystemAgentChanged(false)
          showToast('Artificial Intelligence setting saved successfully')
          if (onSystemAgentEnabledChange) {
            onSystemAgentEnabledChange()
          }
        },
        onError: (error) => {
          showToast('Error saving Artificial Intelligence setting', 'error')
          console.error('Error saving system agent setting:', error)
        },
      })
    }

    // Save project details if changed
    if (isProjectDetailsChanged) {
      useUpdateProjectDetailsHook.execute({
        id: projectId,
        data: { projectName, projectDescription, creativeness },
        onSuccess: () => {
          setIsProjectDetailsChanged(false)
          showToast('Project details updated successfully')
          if (onSystemAgentEnabledChange) {
            // Use the same callback to refresh the project data after updating details
            onSystemAgentEnabledChange()
          }
        },
        onError: (error) => {
          showToast('Error updating project details', 'error')
          console.error('Error updating project details:', error)
        },
      })
    }

    closeModal()
  }

  const onDeleteRule = (rule: any) => {
    if (window.confirm('Are you sure you want to delete this access rule?')) {
      deleteAccessFromProject.execute({
        id: projectId,
        data: { projectId, ruleId: rule.id },
        onSuccess: () => {
          showToast('Rule deleted successfully')
          onAccessRulesUpdate()
        },
        onError: (error) => {
          showToast('Error deleting rule', 'error')
          console.error('Error deleting rule:', error)
        },
      })
    }
  }

  // Define Tailwind-based pt for components (reuse or extend from main.tsx)
  const buttonPt = {
    root: {
      className: 'p-2 px-4 rounded-md text-sm font-medium transition-colors',
    },
  }

  const inputTextPt = {
    root: {
      className:
        'w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500',
    },
  }

  const dropdownPt = {
    root: { className: 'w-full border border-gray-300 rounded-md' },
      input: { className: 'p-2 w-full' },
    list: { className: 'bg-white border border-gray-200 rounded-md shadow-lg' },
    item: { className: 'p-2 hover:bg-gray-100 cursor-pointer' },
  }

  useEffect(() => {
    // This will run after the component mounts
    const removeAllFocus = () => {
      // Remove focus from all input elements
      const inputs = document.querySelectorAll('input, textarea, select');
      inputs.forEach(input => {
        if (input instanceof HTMLElement) {
          input.blur();
        }
      });
    };

    // Call the function after a short delay to ensure the DOM is fully rendered
    const timeoutId = setTimeout(removeAllFocus, 100);

    // Clean up the timeout on component unmount
    return () => clearTimeout(timeoutId);
  }, []); // Empty dependency array means this runs once on mount

  const toggleButtonPt = {
    root: { className: 'my-4' },
  }
  return (
    <div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="access">Access</TabsTrigger>
          <TabsTrigger value="environment">Environment</TabsTrigger>
          <TabsTrigger value="data">Data</TabsTrigger>
          <TabsTrigger value="publishing">Publishing</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          {/* Show loading or fallback if settings are missing */}
          {(editedAccessLevels.length > 0 || isSystemAgentChanged || isProjectDetailsChanged) && (
            <div className="mb-4 p-2 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded-md flex items-center">
              <FaInfoCircle className="mr-2" />
              <span>
                {editedAccessLevels.length > 0
                  ? `${editedAccessLevels.length} access changes await saving`
                  : isSystemAgentChanged 
                  ? 'Agency setting changed'
                  : 'Project details changed'}
              </span>
            </div>
          )}

          <div className="mb-6">
            <h4 className="font-semibold mb-2">Project Details</h4>
            <div className="space-y-4">
              <div>
                <Label htmlFor="project-name">Project Name</Label>
                <Input
                  id="project-name"
                  value={projectName}
                  onChange={(e) => {
                    setProjectName(e.target.value)
                    handleProjectDetailsChange()
                  }}
                  className="mt-1"
                  autoFocus={false}
                  placeholder="Enter project name"
                />
              </div>
              <div>
                <Label htmlFor="project-description">Project Description</Label>
                <Input 
                  id="project-description"
                  value={projectDescription}
                  onChange={(e) => {
                    setProjectDescription(e.target.value)
                    handleProjectDetailsChange()
                  }}
                  className="mt-1"
                  autoFocus={false}
                  placeholder="Enter project description"
                />
              </div>
              <div>
                <Label>Project Type</Label>
                <div className="mt-1 p-2 bg-gray-50 rounded border">
                  <span className="text-sm text-gray-700">
                    {isOrganizationProject ? '🏢 Organization Project' : '👤 Personal'}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Project type cannot be changed after creation
                  </p>
                </div>
              </div>
              {organizationName && (
                <div>
                  <Label>Organization</Label>
                  <div className="mt-1 p-2 bg-gray-50 rounded border">
                    <span className="text-sm text-gray-700">
                      🏢 {organizationName}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Organization assignment cannot be changed after creation
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">
              Project background processing state
            </h4>
            <div className="flex items-center">
              <div className="flex items-center space-x-2">
                <Switch
                  id="system-agent-enabled"
                  checked={agentEnabled}
                  onCheckedChange={onSystemAgentToggle}
                />
                <Label htmlFor="system-agent-enabled" style={{ marginTop: '3px' }}>Enable Artificial Intelligence </Label>
              </div>
              <span className="text-sm text-gray-600 ml-2" style={{ marginTop: '3px' }}>
                {agentEnabled
                  ? '[ Background processing is active ]'
                  : '[ Background processing inactive ]'}
              </span>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold mb-2">Creativeness Level</h4>
            <input
              id="creativeness-slider"
              type="range"
              min={0}
              max={100}
              step={1}
              value={creativeness}
              onChange={handleCreativenessChange}
              className="w-4/5 my-2"
            />
            <span className="creativeness-value">{creativeness}</span>
            <div className="creativeness-desc">
              {getCreativenessDescription(creativeness)}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="environment" className="space-y-4">
          <div className="mb-6">
            <h4 className="font-semibold mb-2">Environment Variables</h4>
            <p className="text-sm text-gray-600 mb-4">
              Manage key-value pairs for project configuration and parameters.
            </p>
            
            {/* Add new environment variable */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
              <Input
                placeholder="Variable key (e.g., API_KEY)"
                value={newEnvKey}
                onChange={(e) => setNewEnvKey(e.target.value)}
              />
              <Input
                placeholder="Variable value"
                value={newEnvValue}
                onChange={(e) => setNewEnvValue(e.target.value)}
              />
              <Button onClick={addEnvironmentVar} disabled={!newEnvKey.trim() || !newEnvValue.trim()}>
                <FaPlus className="mr-2" /> Add
              </Button>
            </div>

            {/* List existing environment variables */}
            <div className="space-y-2">
              {environmentVars.length === 0 ? (
                <p className="text-gray-500 text-sm">No environment variables defined</p>
              ) : (
                environmentVars.map((envVar: any) => (
                  <div key={envVar.id} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex-1">
                      <code className="text-sm font-mono text-blue-600">{envVar.key}</code>
                      <span className="mx-2">=</span>
                      <span className="text-sm text-gray-700">{envVar.value}</span>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteEnvironmentVar(envVar.id)}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="data" className="space-y-4">
          <div className="mb-6">
            <h4 className="font-semibold mb-2">Project Data Management</h4>
            <p className="text-sm text-gray-600 mb-6">
              Export your project data for backup or sharing, or import data from another project.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Export Section */}
              <div className="border rounded-lg p-4">
                <h5 className="font-medium mb-3 flex items-center">
                  <FaDownload className="mr-2 text-blue-600" />
                  Export Project
                </h5>
                <p className="text-sm text-gray-600 mb-4">
                  Download a JSON file containing your project settings, environment variables, and configuration.
                </p>
                <Button 
                  onClick={exportProject}
                  disabled={isExporting}
                  className="w-full"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <FaDownload className="mr-2" />
                      Export Project Data
                    </>
                  )}
                </Button>
              </div>

              {/* Import Section */}
              <div className="border rounded-lg p-4">
                <h5 className="font-medium mb-3 flex items-center">
                  <FaUpload className="mr-2 text-green-600" />
                  Import Project
                </h5>
                <p className="text-sm text-gray-600 mb-4">
                  Upload a project JSON file to import its <strong>contents</strong> (nodes, flows, settings, and environment variables). <strong>Project name, description, and system settings will NOT be changed.</strong> Your original project data remains safe - imported data is only applied when you click "Save". Refresh the page to discard imported changes.
                </p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={importProject}
                  className="hidden"
                  disabled={isImporting}
                  aria-label="Import project data file"
                  title="Import project data file"
                />
                
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImporting}
                  variant="outline"
                  className="w-full"
                >
                  {isImporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <FaUpload className="mr-2" />
                      Import Project Data
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Warning Section */}
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex">
                <FaInfoCircle className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800 mb-1">Important Notes:</p>
                  <ul className="text-yellow-700 space-y-1 list-disc ml-4">
                    <li>Exported data includes project settings, environment variables, and configuration</li>
                    <li>Importing will overwrite current project settings (after confirmation)</li>
                    <li>Remember to save changes after importing to apply the new settings</li>
                    <li>Project structure and actual content (nodes, flows) are not included in exports</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Keep existing Access and Publishing tabs content */}
        <TabsContent value="access" className="space-y-4">
          {/* Move existing access rules content here */}
          {settings && (
            <div className="mb-12">
              {accessRules.map((rule, idx) => (
                <div
                  key={`access-rule-${idx}`}
                  className="py-2 border-b flex justify-between items-center"
                >
                  <div>{rule.name}</div>
                  <div>
                    <Select
                      value={getCurrentAccessLevel(rule)}
                      onValueChange={(accessLevel) =>
                        onAccessLevelChange(rule, accessLevel as AccessLevel)
                      }
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select access level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={AccessLevel.Read}>Read</SelectItem>
                        <SelectItem value={AccessLevel.Edit}>Edit</SelectItem>
                        <SelectItem value={AccessLevel.Full}>Full</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Button
                      variant="destructive"
                      onClick={() => onDeleteRule(rule)}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </div>
              ))}

              <div className="mt-4 flex">
                <Input
                  placeholder="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  autoFocus={false}
                />
                <Select
                  defaultValue={AccessLevel.Full}
                  value={selectedAccessLevel}
                  onValueChange={(accessLevel) =>
                    setSelectedAccessLevel(accessLevel as AccessLevel)
                  }
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select access level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={AccessLevel.Read}>Read</SelectItem>
                    <SelectItem value={AccessLevel.Edit}>Edit</SelectItem>
                    <SelectItem value={AccessLevel.Full}>Full</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="default" onClick={onAddNewRule}>
                  Add User
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="publishing" className="space-y-4">
          <div className="mb-6">
            <h4 className="font-semibold mb-2">Publishing Settings</h4>
            <p className="text-sm text-gray-600 mb-4">
              Manage deliverables and publishing options (Coming soon).
            </p>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex space-x-2 place-content-end mt-6">
        <Button type="submit" onClick={onSettingsSave}>
          Save
        </Button>
        <Button type="submit" onClick={closeModal}>
          Close
        </Button>
      </div>
      <Toaster />
    </div>
  )
}

export default memo(ProjectSettings)
